import styled, { css, keyframes } from "styled-components";
import { colors } from "../../utils";
import { device } from "../../utils/device";
import { gutterX, spacing } from "../../theme/tokens";

export const HeaderMainDiv = styled.div`
  /* 100vw INCLUDES the classic scrollbar gutter, so this header was always
     ~15px wider than its own scroll container — a permanent horizontal
     overflow. 100% is the same width without the overflow. */
  width: 100%;
  height: 8vh;
  min-height: 60px;
  display: flex;
  justify-content: space-between;
  /* MEASURED before: 2vw = 28.8px @1440 but 7.8px @390 — the Edit Wheel header
     lost its side margin as the viewport narrowed. Floor it. */
  padding: 0.8vh ${gutterX("2vw")};
  background-color: #ffffff;
  align-items: center;
  display: grid;
  /* vy-0914-ew-builder-header-title-under-presence-avatar-1440 (ISP-9999).
     MEASURED (Playwright, web-dev.iyuga.net/builder/6264, uid 12675): at
     1440x900 — desktop width, none of the media rules below match — the
     WheelPresence "QT" avatar + CoeditStatus's "Updated — Refresh" pill (both
     builder/collab-only content) pushed RightContainer past a fixed 480px.
     Grid doesn't clip or reflow a track's overflow, so the flex-end-aligned
     RightContainer spilled LEFTWARD past its own column boundary and painted
     over the title text (title.right 847.5, avatar.x as low as 817.2) instead
     of the middle track shrinking to make room for it. Same defect class,
     same fix shape, as the mobileLandscape/$responsive/portrait breakpoints
     below — content-size the right column (minmax floor keeps the normal,
     no-badges case pixel-identical at exactly 480px) and make the middle
     track's minmax(0, 1fr) explicit so the title's pre-existing
     text-overflow: ellipsis (TextView) actually gets a narrower box to
     truncate against instead of being silently painted over. */
  grid-template-columns: 480px minmax(0, 1fr) minmax(480px, max-content);

  /* gordon-glitch-header-title-overlaps-badges-1024w (ISP-9999). MEASURED
     (Playwright, web-dev.iyuga.net/builder, wheel 5595, uid 12675): at
     1024x780/1024x768 the title ran UNDER the badge cluster — title.right
     649.77 > badge.left 616.72 / 552.80. device.mobileLandscape's max-width
     of 1024px in a sideways orientation also matches ordinary tablet widths
     (not just small phones held sideways —
     those are already gated out of the builder below 768 by
     builderMobileGate). This rule's flat 220px side columns are narrower than
     the badge cluster's real content (coedit/autosave chips + presence
     avatars + action icons); RightContainer has no overflow clipping, so it
     spilled LEFTWARD onto the title's track instead of shrinking/wrapping.
     Content-size the side columns and let the centre shrink — the same
     pattern already proven below for the $responsive (<=1199) and
     tablet-portrait (<=976) breakpoints — so title.right can never exceed the
     badge cluster's left edge. */
  @media (${device.mobileLandscape}) {
    grid-template-columns: auto minmax(0, 1fr) auto;
    column-gap: 8px;
    > * {
      min-width: 0;
    }
    height: 60px;
    transition: margin-top 0.3s ease-in-out;
    ${({ hide }) =>
      hide &&
      css`
        margin-top: -60px;
      `}
  }

  ${({ isMobileLandscape }) =>
    isMobileLandscape &&
    `
      height: 60px;
    transition: margin-top 0.3s ease-in-out;
    ${({ hide }) =>
      hide &&
      css`
        margin-top: -60px;
      `}
    `}

  /* U2 responsive (flag-gated, builder only): the fixed 480px 1fr 480px grid is
     960px of hard columns — it overflows any tablet-portrait viewport (the
     existing mobileLandscape override only helps in LANDSCAPE). Replace with
     content-sized side columns + a shrinkable centre at <=1199 so the header
     never forces horizontal scroll. Scoped to <=1199 ⇒ desktop pixel-locked;
     inert (byte-identical) when $responsive is falsy. Only the builder passes
     $responsive — ViewWheel's header is untouched. */
  ${({ $responsive }) =>
    $responsive &&
    css`
      @media (max-width: 1199px) {
        width: 100%;
        grid-template-columns: auto minmax(0, 1fr) auto;
        column-gap: 8px;
        > * {
          min-width: 0;
        }
      }
    `}

  /* ViewWheel (non-builder) tablet-portrait overflow fix. MEASURED on the live
     sandbox bundle main.cdc3f457.js (staging wheel 2636, anon): in the ~800-976px
     PORTRAIT band the header's fixed 480px 1fr 480px grid = 960px of hard columns,
     which overflows every viewport narrower than ~976px — documentElement.scrollWidth
     pinned ~976px at 820/850/900 (170/141/92px of page overflow) with the 480px right
     column spilling past the viewport. The mobileLandscape override (220 1fr 220)
     only helps in LANDSCAPE, and the $responsive fix above is builder-only, so
     ViewWheel's portrait header had no containment. Content-size the side columns and
     let the centre shrink so the header always fits. Scoped to portrait <=976 so the
     >=977 desktop grid and the landscape override are both untouched. */
  @media (max-width: 976px) and (orientation: portrait) {
    grid-template-columns: auto minmax(0, 1fr) auto;
    column-gap: 8px;
    > * {
      min-width: 0;
    }
  }
`;

export const LeftContainer = styled.div`
  height: 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RightContainer = styled.div`
  height: 40px;
  display: flex;
  justify-content: flex-end;
`;

// gordon-1002-P2 (2026-10-02, Gordon screenshot, DEVELOPMENT
// web-dev.iyuga.net/builder/4908): "margin between the Avatar Username (GM),
// Gallery, Preview is inconsistent. It should be tighter." — on the builder
// header each cluster member (the IconWrapper tiles, ChatNavEntry's own
// IconWrapper, the AutosaveChip wrapper, ProfileMenu's avatar) bakes in its
// OWN asymmetric margin (0px 3px / 0px 4px / marginLeft 8 / margin-left 10),
// so consecutive gaps measured anywhere from 3px to 10px. `$tight` makes ONE
// flex `gap` (the design-system's tightest token, spacing.xs) the sole
// source of space between adjacent items; index.jsx neutralises each item's
// own baked-in margin at its render site (inline overrides where WheelHeader
// renders the node itself, a cancelling negative margin on a wrapper where it
// doesn't — see the gordon-1002-P2 comments there for why a blanket `> *`
// CSS override isn't used: jsdom's cascade doesn't reliably let a
// `!important` child-combinator rule beat a plain class rule, so margins
// are neutralised per item instead). Scoped to the builder header only
// ($tight = isEditScreen) — ViewWheel/Gallery headers (not $tight) are
// byte-identical to before.
export const HEADER_RIGHT_CLUSTER_GAP_PX = spacing.xs;

export const RightCluster = styled.div`
  margin-left: 16px;
  display: flex;
  align-items: center;

  ${({ $tight }) =>
    $tight &&
    css`
      gap: ${HEADER_RIGHT_CLUSTER_GAP_PX}px;
    `}
`;

export const TitleDiv = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;

  /* Supersedes PR #2430. MEASURED on the live sandbox bundle
     (main.345736b5.js, wheel 4116, anon): a wheel title with NO whitespace
     (>=140 chars) blew the page out horizontally at every width from ~800px
     up — documentElement.scrollWidth 2691 vs clientWidth 1426 @1440
     (= 1265px of page overflow), with WheelHeader's 480px RightContainer
     rendered at left:2211, entirely outside the viewport.

     Root cause: HeaderMainDiv is grid-template-columns: 480px 1fr 480px.
     A grid item's default min-width: auto sizes the 1fr track to its
     content's MIN-CONTENT width; an unbreakable string has no break
     opportunity, so min-content == the full text width, which forces the
     track wide and shoves the fixed right column off-screen. TextView
     already carries -webkit-box + overflow: hidden + line-clamp, but it
     never gets a chance to truncate because nothing above it is ever
     constrained down.

     Fix: min-width: 0 down the flex chain so the 1fr track can actually
     shrink and the pre-existing clamp takes effect. NOTE: deliberately NOT
     overflow: hidden here (#2430 proposed that) — TitleDiv is the
     positioning context for the absolutely-positioned author / clone-lineage
     popup, which overflow: hidden would clip. */
  min-width: 0;

  & p {
    margin: 0;
  }
`;

export const Title = styled.p`
  font-family: "DM Sans";
  font-size: 20px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #123051;
`;

export const Subtitle = styled.span`
  font-family: "DM Sans";
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  color: #565556;
`;

// Gordon 2026-08-25 (screenshots, /wheel/4864): the View-Wheel header action
// icons rendered at every intrinsic size their source SVG/MUI-glyph happened to
// declare — Home 22, Discover-compass glyph 22, Search 22, Edit-pen 24, Translate
// 24, Share 28, Mail/envelope 26 — so the top toolbar read as a ragged row of
// mismatched icons. The wrapper box was already a uniform 42x42/8px-padding tile;
// only the glyph inside varied. `$normalizeIcons` (opted into by ViewWheel; the
// builder header does NOT pass it, so its header stays byte-identical) pins EVERY
// glyph in the wrapper — SVG or <img>, however deeply nested (e.g. the Edit pen
// lives inside an <a>) — to a single square equal to the Edit-pen's own width
// (24px, the reference the row is normalized to). Wrapper geometry, hover
// highlight and the >=32px hit area are untouched, so only the visible glyph
// changes size and touch targets are preserved.
export const HEADER_ICON_GLYPH_PX = 24;
export const HEADER_TILE_PX = 44;

export const IconWrapper = styled.div`
  height: 42px;
  width: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 16px;
  cursor: pointer;
  border-radius: 4px;
  padding: 8px;
  margin: 0px 3px;
  transition: box-shadow 0.2s ease;
  ${({ boolean }) =>
    boolean &&
    css`
      background-color: ${colors.background};
    `}

  ${({ $normalizeIcons }) =>
    $normalizeIcons &&
    css`
      & svg,
      & img {
        width: ${HEADER_ICON_GLYPH_PX}px !important;
        height: ${HEADER_ICON_GLYPH_PX}px !important;
      }
      /* T22.727: View-Wheel toolbar tiles (Info, Clone, Favorite, Share...)
         were 42x42, under the 44px touch floor. Only the ViewWheel header opts
         in; the builder header stays 42. */
      width: ${HEADER_TILE_PX}px;
      height: ${HEADER_TILE_PX}px;
    `}


  /* T22.727: the left-cluster tiles (Wheel Info, Business Box) are not in the
     rightContent map, so they never got $normalizeIcons and stayed 42x42.
     $touchTile only lifts the box to the 44px floor; the glyph is untouched. */
  ${({ $touchTile }) =>
    $touchTile &&
    css`
      width: ${HEADER_TILE_PX}px;
      height: ${HEADER_TILE_PX}px;
    `}

  &:hover {
    background-color: ${colors.background};
  }

  &:active {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  }
`;

// The Mail/Invite (envelope) tile is rendered by EnterBetaButton (a shared
// component reused unmodified by the Gallery header), NOT through the
// rightContent IconWrapper map, so IconWrapper's $normalizeIcons rule cannot
// reach its glyph. This span applies the SAME 24px glyph pin ONLY inside the
// ViewWheel header (opted in via $normalizeIcons), leaving EnterBetaButton's own
// default (fontSize 26) — and every other call site — untouched.
export const InviteGlyphNormalizer = styled.span`
  display: inline-flex;
  align-items: center;
  /* ISP-9999 — every other header tile pair (IconWrapper) carries
     margin: 0px 3px, giving a uniform 6px gap between tiles. This tile is
     rendered outside the IconWrapper map (see comment above), so without its
     own margin the gap on either side of it collapses to 3px. */
  margin-left: 3px;
  ${({ $normalizeIcons }) =>
    $normalizeIcons &&
    css`
      & svg,
      & img {
        width: ${HEADER_ICON_GLYPH_PX}px !important;
        height: ${HEADER_ICON_GLYPH_PX}px !important;
      }
    `}
`;

// ISP-9999 — wraps the optional per-item trailingStat (e.g. the wheel's
// favorite count) so it carries the same margin: 0px 3px as every
// IconWrapper tile, keeping every gap in the header a uniform 6px. Only
// mounted when trailingStat exists, so trailingStat-less icons render
// byte-identical to before.
export const TrailingStatWrapper = styled.span`
  margin: 0px 3px;
`;

export const Icon = styled.img`
  width: 42px;
  height: 42px;
  cursor: pointer;
  width: 42px;
  height: 42px;
  /* T22.727: View-Wheel logo link opts in to the 44px touch floor. */
  ${({ $touchTile }) =>
    $touchTile &&
    css`
      width: ${HEADER_TILE_PX}px;
      height: ${HEADER_TILE_PX}px;
    `}
`;

export const AuthorContainer = styled.div`
  position: relative;
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

export const Beta = styled.img`
  width: 70px;
  height: 32px;
  margin-right: 10px;
  cursor: pointer;

  /* Add the pulse animation */
  animation: ${pulse} 1.5s infinite;
`;
